/**
 * Generic SSE consumer over `fetch()` + ReadableStream.
 *
 * Why not EventSource?
 * --------------------
 * EventSource cannot set custom HTTP headers, which forced the
 * previous design to put the JWT in the URL (`?token=<jwt>`). That
 * landed credentials in nginx + Cloud Logging access logs — a
 * CNBV / SEC log-leak finding. `fetch()` + ReadableStream lets us
 * send `Authorization: Bearer` like every other API call.
 *
 * Wire format
 * -----------
 * Server still emits SSE framing:
 *
 *   data: {"type":"...","trigger_widgets":[...],...}\n\n
 *   : keepalive\n\n
 *
 * We use a tiny inline parser (no library) because the format we
 * accept is constrained: single-line `data:` only, `:` comments are
 * keepalives we ignore, no `event:` / `id:` / `retry:`. A ~30-LOC
 * parser is more auditable than a 5KB dep and our wire stays simple
 * enough that edge cases don't matter.
 *
 * Reconnect lifecycle
 * -------------------
 * Unlike EventSource there's no built-in auto-reconnect. We
 * orchestrate it: on stream end / network error / 401, abort the
 * fetch, refresh the JWT if needed, open a new fetch. Bounded
 * retries (`MAX_CONSECUTIVE_FAILURES`) prevent a runaway loop
 * against a hard-down server; any successful event resets the
 * counter.
 */
import { useEffect, useRef, useState } from 'react';

import authService, { RefreshWithheld } from '../services/auth.service';
import { readPersistedAccessToken } from '../stores/authStore';

export interface RealtimeEvent<WidgetKey extends string> {
  type: string;
  occurred_at: string;
  resource_id?: string;
  trigger_widgets: WidgetKey[];
  /** Present on staff-channel events — identifies which agent owned
   *  the originating mutation. The agent-channel payloads omit it. */
  agent_id?: string;
  extra?: Record<string, unknown>;
}

export type RefetchMap<WidgetKey extends string> = Partial<
  Record<WidgetKey, () => void | Promise<void>>
>;

/** Min delay before attempting a manual reopen after an error. Floors
 *  the reconnect rate so a hard-down server doesn't get hammered. */
const RECONNECT_BACKOFF_MS = 2000;

/** Maximum consecutive errors (with no successful event in between)
 *  before we stop reopening. The user sees stale widgets until they
 *  refresh — preferable to an infinite reconnect storm. */
const MAX_CONSECUTIVE_FAILURES = 5;


// ─── Auth helpers ─────────────────────────────────────────────────

/** How long to wait before looking again when the token could not be renewed because the
 *  person is not there (see `refreshTokens`). Not a failure: it is not counted. */
const WAITING_FOR_THE_PERSON_MS = 20_000;

/** Refresh the access token (the auth service keeps the new pair).
 *  'withheld' means nobody has touched the screen since the last refresh: the stream is not
 *  allowed to keep a session alive, so it waits for the person instead of asking. */
async function refreshTokens(): Promise<'renewed' | 'withheld' | 'failed'> {
  try {
    await authService.refreshToken();
    return 'renewed';
  } catch (error) {
    if (error instanceof RefreshWithheld) return 'withheld';
    // Refresh failed — the axios interceptor handles the global
    // session-expired flow on the user's next API call.
    return 'failed';
  }
}


// ─── SSE wire parser (hand-rolled, ~30 LOC) ───────────────────────
//
// Format we accept:
//
//   data: <json>\n\n
//   : <anything>\n\n          (keepalive comment, ignored)
//
// We don't handle `event:`, `id:`, `retry:`, multi-line `data:`, or
// CRLF — none of which the server emits. If the wire format ever
// grows those features, swap this for `eventsource-parser` (~3KB).

/** Feed bytes into the parser; yields parsed JSON payloads as
 *  whole frames complete (`\n\n` boundary). Holds partial frames in
 *  the buffer between calls so chunk boundaries don't lose data. */
function makeSseParser<WidgetKey extends string>(
  onEvent: (event: RealtimeEvent<WidgetKey>) => void,
): (chunk: string) => void {
  let buffer = '';
  return (chunk: string): void => {
    buffer += chunk;
    let boundary: number;
    while ((boundary = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      if (!frame || frame.startsWith(':')) continue; // keepalive / comment
      if (!frame.startsWith('data: ')) continue;     // unknown field, skip
      try {
        onEvent(JSON.parse(frame.slice(6)) as RealtimeEvent<WidgetKey>);
      } catch {
        // Malformed payload — the next event will arrive. Ignoring
        // here is safer than crashing the stream consumer.
      }
    }
  };
}

/**
 * Map of event-type string (e.g. "appointment.imminent") → handler.
 * Consumers register handlers for events that need an action beyond
 * widget refetch — browser Notifications, toasts, page nav. The map
 * is `Partial` so consumers only handle the types they care about;
 * unknown event types are ignored.
 */
export type EventTypeHandlerMap<WidgetKey extends string> = Partial<
  Record<string, (event: RealtimeEvent<WidgetKey>) => void>
>;

/** Build the event handler that fires refetcher(s) AND any
 *  registered type-specific handler. A misbehaving handler is
 *  logged but does not tear down the stream. */
function makeEventHandler<WidgetKey extends string>(
  refetchersRef: { current: RefetchMap<WidgetKey> },
  onEventTypeRef: { current: EventTypeHandlerMap<WidgetKey> | undefined },
): (event: RealtimeEvent<WidgetKey>) => void {
  return (event) => {
    const handler = onEventTypeRef.current?.[event.type];
    if (handler) {
      try {
        handler(event);
      } catch (err) {
        console.error('useEventStream onEventType handler threw', err);
      }
    }
    const widgets = event.trigger_widgets ?? [];
    for (const widget of widgets) {
      const refetch = refetchersRef.current[widget];
      if (refetch) {
        refetch();
      }
    }
  };
}


// ─── Stream lifecycle (module scope so nesting stays shallow) ─────

interface StreamHandle<WidgetKey extends string> {
  url: string;
  refetchersRef: { current: RefetchMap<WidgetKey> };
  onEventTypeRef: { current: EventTypeHandlerMap<WidgetKey> | undefined };
  scheduleReopen: () => void;
  abort: AbortController;
  cancelled: boolean;
  consecutiveFailures: number;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
}

/** Open the SSE fetch, consume the body, dispatch parsed events.
 *  Returns nothing — completion (clean end OR error) triggers the
 *  reconnect / give-up logic in `scheduleNextOpen`. */
async function runStream<WidgetKey extends string>(
  handle: StreamHandle<WidgetKey>,
): Promise<'withheld' | void> {
  // The token every tab shares: another tab may have renewed it since this one last did.
  const token = readPersistedAccessToken();
  if (!token) return;

  let resp: Response;
  try {
    resp = await fetch(handle.url, {
      headers: { Authorization: `Bearer ${token}` },
      signal: handle.abort.signal,
    });
  } catch {
    // Network failure / abort — treat as a single failure tick.
    return;
  }

  if (resp.status === 401) {
    // JWT expired between page load and stream open. Refresh, then
    // let the reconnect path open with the new token.
    const refreshed = await refreshTokens();
    if (refreshed === 'withheld') return 'withheld';
    if (refreshed === 'failed') {
      // Hard auth failure — stop trying. Axios interceptor will
      // surface session-expired on the user's next call.
      handle.consecutiveFailures = MAX_CONSECUTIVE_FAILURES + 1;
    }
    return;
  }

  if (!resp.ok || !resp.body) return;

  const onEvent = makeEventHandler(handle.refetchersRef, handle.onEventTypeRef);
  const feed = makeSseParser(onEvent);
  const reader = resp.body
    .pipeThrough(new TextDecoderStream())
    .getReader();

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) return;
      if (value) {
        handle.consecutiveFailures = 0;
        feed(value);
      }
    }
  } catch {
    // Stream broke (network drop, abort). Reconnect path picks up.
  } finally {
    reader.releaseLock();
  }
}

/** Decide whether to reopen after a stream finished, and schedule it. */
function scheduleNextOpen<WidgetKey extends string>(
  handle: StreamHandle<WidgetKey>,
): void {
  if (handle.cancelled) return;
  handle.consecutiveFailures += 1;
  if (handle.consecutiveFailures > MAX_CONSECUTIVE_FAILURES) {
    // Give up — server is probably down. Stale widgets are less
    // harmful than a runaway reconnect loop.
    return;
  }
  handle.reconnectTimer = setTimeout(handle.scheduleReopen, RECONNECT_BACKOFF_MS);
}

async function openAndManage<WidgetKey extends string>(
  handle: StreamHandle<WidgetKey>,
): Promise<void> {
  const outcome = await runStream(handle);
  if (outcome === 'withheld') {
    if (!handle.cancelled) {
      handle.reconnectTimer = setTimeout(handle.scheduleReopen, WAITING_FOR_THE_PERSON_MS);
    }
    return;
  }
  scheduleNextOpen(handle);
}

function disposeStream<WidgetKey extends string>(
  handle: StreamHandle<WidgetKey>,
): void {
  handle.cancelled = true;
  if (handle.reconnectTimer !== null) {
    clearTimeout(handle.reconnectTimer);
  }
  handle.abort.abort();
}


// ─── Hook ─────────────────────────────────────────────────────────

/**
 * @param sseUrl SSE endpoint URL (no query string needed; auth is in
 *   the Authorization header). Pass `null` to disable the
 *   subscription entirely (e.g. before we know the user's id).
 * @param refetchers map of widget keys → refetch fn. The hook only
 *   calls fns whose keys appear in the map — unknown widgets are
 *   ignored so the server can ship new event types ahead of frontend
 *   support.
 * @param onEventType optional map of event-type string → handler. Use
 *   for events that need a UI action beyond widget refetch — e.g.
 *   `appointment.imminent` → fire a browser Notification + toast.
 *   Unknown types are ignored.
 */
export function useEventStream<WidgetKey extends string>(
  sseUrl: string | null,
  refetchers: RefetchMap<WidgetKey>,
  onEventType?: EventTypeHandlerMap<WidgetKey>,
): void {
  // Keep the latest refetchers in a ref so the stream opened once on
  // mount can read current fns without resubscribing every parent
  // render.
  const refetchersRef = useRef<RefetchMap<WidgetKey>>(refetchers);
  refetchersRef.current = refetchers;

  const onEventTypeRef = useRef<EventTypeHandlerMap<WidgetKey> | undefined>(onEventType);
  onEventTypeRef.current = onEventType;

  // Bumps on every manual reopen so the effect re-runs with a fresh
  // fetch. The `sseUrl` prop itself doesn't change on reconnect.
  const [reopenGeneration, setReopenGeneration] = useState(0);

  useEffect(() => {
    if (!sseUrl) return;
    const handle: StreamHandle<WidgetKey> = {
      url: sseUrl,
      refetchersRef,
      onEventTypeRef,
      scheduleReopen: () => setReopenGeneration((g) => g + 1),
      abort: new AbortController(),
      cancelled: false,
      consecutiveFailures: 0,
      reconnectTimer: null,
    };
    openAndManage(handle);
    return () => disposeStream(handle);
  }, [sseUrl, reopenGeneration]);
}
